import React, { useCallback, useEffect, useRef, useState } from "react";
import { Phone, Navigation, Camera, CheckCircle2, BellRing, LogOut, Clock, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { startRing, stopRing } from "@/lib/ring";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const capi = () => axios.create({ baseURL: BASE, headers: { Authorization: `Bearer ${localStorage.getItem("chela_token") || ""}` } });

const T = {
  hi: { login: "CHELA लॉगिन", phone: "मोबाइल नंबर", pin: "PIN", go: "लॉगिन करें", hello: "नमस्ते", newjob: "नया जॉब आया है!", accept: "स्वीकार करें", decline: "मना करें", left: "बचा समय", none: "अभी कोई जॉब नहीं। नया जॉब आते ही फ़ोन बजेगा।", my: "मेरे जॉब", call: "ग्राहक को कॉल करें", nav: "लोकेशन पर जाएँ", reached: "मैं पहुँच गया", before: "काम से पहले फ़ोटो", after: "काम के बाद फ़ोटो", done: "काम पूरा हुआ", earn: "मेरी कमाई", paid: "पेमेंट मिल गया", due: "पेमेंट बाकी", notif: "नोटिफ़िकेशन और रिंग चालू करें", notifOn: "नोटिफ़िकेशन चालू हैं", logout: "लॉगआउट", needPhotos: "पहले दोनों फ़ोटो लें", sharing: "आपकी लाइव लोकेशन ग्राहक को दिख रही है — ऐप खुला रखें", rules: "ग्राहक से कभी कैश या UPI PIN न माँगें। पेमेंट पहले ही हो चुका है।", travel: "ग्राहक के साथ दूर गए? किलोमीटर डालें (₹3/किमी)", km: "किमी", sendTravel: "ट्रैवल चार्ज भेजें", travelSent: "ग्राहक को पेमेंट लिंक भेजा गया", travelPaid: "ट्रैवल चार्ज मिल गया" },
  en: { login: "CHELA Login", phone: "Mobile number", pin: "PIN", go: "Log in", hello: "Hello", newjob: "New job!", accept: "Accept", decline: "Decline", left: "Time left", none: "No job right now. Your phone will ring when a new job comes.", my: "My jobs", call: "Call customer", nav: "Go to location", reached: "I have reached", before: "Photo before work", after: "Photo after work", done: "Job done", earn: "My earnings", paid: "Paid", due: "Payment due", notif: "Turn on notifications & ring", notifOn: "Notifications are on", logout: "Log out", needPhotos: "Take both photos first", sharing: "Your live location is visible to the customer — keep the app open", rules: "Never ask the customer for cash or UPI PIN. Payment is already done.", travel: "Went far with the customer? Enter kilometres (₹3/km)", km: "km", sendTravel: "Send travel charge", travelSent: "Payment link sent to customer", travelPaid: "Travel charge paid" },
};

const b64ToU8 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const r = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...r].map((c) => c.charCodeAt(0))); };

function Login({ c, onIn }) {
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const go = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${BASE}/chela/login`, { phone, pin });
      localStorage.setItem("chela_token", data.token);
      onIn();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Login failed"); }
  };
  return (
    <form onSubmit={go} className="mt-10 space-y-4" data-testid="chela-login-form">
      <h1 className="font-display font-extrabold text-3xl text-[#121316]">{c.login}</h1>
      <input data-testid="chela-login-phone" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder={c.phone} className="w-full h-14 px-4 rounded-2xl border border-[#E5E0D8] text-lg" />
      <input data-testid="chela-login-pin" inputMode="numeric" type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={c.pin} className="w-full h-14 px-4 rounded-2xl border border-[#E5E0D8] text-lg tracking-widest" />
      <button data-testid="chela-login-submit" className="w-full h-14 rounded-full btn-saffron font-bold text-lg">{c.go}</button>
    </form>
  );
}

function OfferAlert({ c, job, onAccept, onDecline }) {
  const [left, setLeft] = useState(job.seconds_left);
  useEffect(() => {
    const iv = setInterval(() => setLeft((x) => Math.max(0, x - 1)), 1000);
    return () => clearInterval(iv);
  }, [job.id]);
  return (
    <div data-testid="chela-offer" className="fixed inset-0 z-50 bg-[#C0392B] text-white p-6 flex flex-col animate-pulse-slow">
      <BellRing size={56} className="mx-auto mt-6 animate-bounce" />
      <h2 className="mt-4 text-center font-display font-extrabold text-4xl">{c.newjob}</h2>
      <div className="mt-6 rounded-3xl bg-white text-[#121316] p-5 space-y-2">
        <div className="font-display font-bold text-2xl">{job.service}</div>
        <div className="flex gap-2 items-center"><Clock size={18} /> {job.date} · {job.time} · {job.duration}</div>
        <div className="flex gap-2 items-center"><MapPin size={18} /> {job.area || "Bhopal"}</div>
        {job.description && <div className="text-sm text-[#5A5D65]">{job.description}</div>}
      </div>
      <div className="mt-4 text-center font-bold text-xl" data-testid="chela-offer-timer">{c.left}: {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</div>
      <div className="mt-auto grid gap-3">
        <button data-testid="chela-offer-accept" onClick={onAccept} className="h-20 rounded-full bg-[#1B8A4B] font-extrabold text-2xl">{c.accept}</button>
        <button data-testid="chela-offer-decline" onClick={onDecline} className="h-14 rounded-full bg-white/15 font-bold">{c.decline}</button>
      </div>
    </div>
  );
}

function PhotoBtn({ label, has, onFile, testid }) {
  return (
    <label className={`h-14 rounded-full font-bold inline-flex items-center justify-center gap-2 cursor-pointer ${has ? "bg-[#E8F7EE] text-[#1B8A4B]" : "bg-[#121316] text-white"}`}>
      {has ? <CheckCircle2 size={20} /> : <Camera size={20} />} {label}
      <input data-testid={testid} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
    </label>
  );
}

function useShareLocation(job) {
  useEffect(() => {
    if (job.job_status !== "assigned" || !navigator.geolocation) return undefined;
    let last = 0;
    const id = navigator.geolocation.watchPosition((p) => {
      if (Date.now() - last < 15000) return;
      last = Date.now();
      capi().post(`/chela/jobs/${job.id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {});
    }, () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [job.id, job.job_status]);
}

function TravelBox({ c, job, onSent }) {
  const [km, setKm] = useState(job.travel?.km || "");
  if (job.travel?.status === "paid") { return <div data-testid="chela-travel-paid" className="text-sm font-bold text-[#1B8A4B] bg-[#E8F7EE] rounded-xl px-3 py-2">{c.travelPaid}: ₹{job.travel.amount}</div>; }
  const send = async () => {
    try {
      const { data } = await capi().post(`/chela/jobs/${job.id}/travel`, { km: Number(km) });
      toast.success(`₹${data.amount}`);
      if (!data.wa_auto) { window.open(`https://wa.me/91${data.customer_phone}?text=${encodeURIComponent(`Travel charge ${data.km} km x ₹3 = ₹${data.amount}. Please pay here: ${data.link}`)}`, "_blank"); }
      onSent();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  return (
    <div className="rounded-2xl border border-[#E5E0D8] p-3 space-y-2">
      <div className="text-sm font-bold text-[#121316]">{c.travel}</div>
      <div className="flex gap-2">
        <input data-testid="chela-travel-km" inputMode="decimal" value={km} onChange={(e) => setKm(e.target.value.replace(/[^\d.]/g, "").slice(0, 5))} placeholder={c.km} className="w-24 h-12 px-3 rounded-xl border border-[#E5E0D8] text-lg" />
        <button data-testid="chela-travel-send" onClick={send} disabled={!Number(km)} className="flex-1 h-12 rounded-full btn-dark font-bold disabled:opacity-50">{c.sendTravel}{Number(km) ? ` ₹${Math.round(Number(km) * 3)}` : ""}</button>
      </div>
      {job.travel?.status === "pending" && <div className="text-xs text-[#8A6A2E]">{c.travelSent}: ₹{job.travel.amount}</div>}
    </div>
  );
}

function ActiveJob({ c, job, act, upload, reload }) {
  useShareLocation(job);
  const both = job.photos?.before && job.photos?.after;
  return (
    <div data-testid={`chela-job-${job.id}`} className="rounded-3xl bg-white border border-[#E5E0D8] p-5 space-y-3">
      <div className="font-display font-bold text-xl text-[#121316]">{job.service} <span className="text-sm text-[#8A8D95]">#{job.ref}</span></div>
      <div className="text-[#3A3D45]">{job.date} · {job.time} · {job.duration}</div>
      <div className="text-[#3A3D45]">{job.customer} · {job.area}</div>
      {job.description && <div className="text-sm text-[#5A5D65]">{job.description}</div>}
      <div className="grid grid-cols-2 gap-3">
        <a data-testid="chela-call" href={job.call} className="h-14 rounded-full bg-[#1B8A4B] text-white font-bold inline-flex items-center justify-center gap-2"><Phone size={20} /> {c.call}</a>
        <a data-testid="chela-nav" href={job.map} target="_blank" rel="noreferrer" className="h-14 rounded-full bg-[#2457A6] text-white font-bold inline-flex items-center justify-center gap-2"><Navigation size={20} /> {c.nav}</a>
      </div>
      {job.job_status === "assigned" && <div data-testid="chela-sharing" className="text-xs font-bold text-[#1B8A4B] bg-[#E8F7EE] rounded-xl px-3 py-2">● {c.sharing}</div>}
      {job.job_status === "assigned" && <button data-testid="chela-reached" onClick={() => act(job.id, "start")} className="w-full h-14 rounded-full btn-dark font-bold">{c.reached}</button>}
      <div className="grid grid-cols-2 gap-3">
        <PhotoBtn testid="chela-photo-before" label={c.before} has={!!job.photos?.before} onFile={(f) => upload(job.id, "before", f)} />
        <PhotoBtn testid="chela-photo-after" label={c.after} has={!!job.photos?.after} onFile={(f) => upload(job.id, "after", f)} />
      </div>
      {job.job_status === "started" && <TravelBox c={c} job={job} onSent={reload} />}
      <button data-testid="chela-done" onClick={() => (both ? act(job.id, "done") : toast.error(c.needPhotos))} className={`w-full h-16 rounded-full font-extrabold text-lg ${both ? "btn-saffron" : "bg-[#E5E0D8] text-[#8A8D95]"}`}>{c.done}</button>
      <p className="text-xs text-[#8A6A2E] bg-[#FCF4E6] rounded-xl px-3 py-2">{c.rules}</p>
    </div>
  );
}

export default function ChelaApp() {
  const { lang, setLang } = useLang();
  const c = T[lang] || T.hi;
  const [authed, setAuthed] = useState(!!localStorage.getItem("chela_token"));
  const [data, setData] = useState(null);
  const [notifOn, setNotifOn] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");
  const ringing = useRef(false);

  const load = useCallback(async () => {
    try { const r = await capi().get("/chela/jobs"); setData(r.data); }
    catch (e) { if (e.response?.status === 401) { localStorage.removeItem("chela_token"); setAuthed(false); } }
  }, []);

  useEffect(() => {
    if (!authed) return undefined;
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [authed, load]);

  useEffect(() => {
    const has = (data?.offers || []).length > 0;
    if (has && !ringing.current) { startRing(); ringing.current = true; }
    if (!has && ringing.current) { stopRing(); ringing.current = false; }
  }, [data]);
  useEffect(() => () => stopRing(), []);

  const enableNotif = async () => {
    startRing(); stopRing();
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Please allow notifications"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data: k } = await axios.get(`${BASE}/push/vapid-key`);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(k.key) });
      await capi().post("/chela/push-subscribe", { subscription: sub.toJSON() });
      setNotifOn(true);
      toast.success(c.notifOn);
    } catch (e) { toast.error("Notifications not supported on this phone/browser"); }
  };

  const act = async (id, action) => {
    try { await capi().post(`/chela/jobs/${id}/${action}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); load(); }
  };
  const upload = async (id, kind, file) => {
    const fd = new FormData(); fd.append("kind", kind); fd.append("photo", file);
    const tid = toast.loading("Uploading…");
    try { await capi().post(`/chela/jobs/${id}/photo`, fd); toast.success("Photo saved", { id: tid }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed", { id: tid }); }
  };

  const offer = data?.offers?.[0];
  return (
    <div className="min-h-screen bg-[#FAF8F5] p-5 pb-12 max-w-md mx-auto">
      <div className="flex items-center justify-between">
        <div className="font-display font-extrabold text-2xl text-[#121316]">CHELA <span className="text-sm text-[#E05624]">Partner</span></div>
        <div className="flex gap-2">
          <button data-testid="chela-lang" onClick={() => setLang(lang === "hi" ? "en" : "hi")} className="h-9 px-3 rounded-full border border-[#E5E0D8] bg-white text-sm font-bold">{lang === "hi" ? "EN" : "हिंदी"}</button>
          {authed && <button data-testid="chela-logout" onClick={() => { localStorage.removeItem("chela_token"); setAuthed(false); setData(null); }} className="h-9 w-9 rounded-full border border-[#E5E0D8] bg-white inline-flex items-center justify-center"><LogOut size={16} /></button>}
        </div>
      </div>
      {!authed && <Login c={c} onIn={() => setAuthed(true)} />}
      {authed && data && (
        <>
          <p className="mt-6 text-lg text-[#121316]">{c.hello}, <b>{data.name}</b></p>
          <button data-testid="chela-enable-notif" onClick={enableNotif} className={`mt-3 w-full h-12 rounded-full font-bold inline-flex items-center justify-center gap-2 ${notifOn ? "bg-[#E8F7EE] text-[#1B8A4B]" : "bg-[#E05624] text-white"}`}><BellRing size={18} /> {notifOn ? c.notifOn : c.notif}</button>
          <h2 className="mt-8 font-display font-bold text-xl text-[#121316]">{c.my}</h2>
          <div className="mt-3 space-y-4">
            {data.active.length === 0 && <p data-testid="chela-no-jobs" className="text-[#5A5D65]">{c.none}</p>}
            {data.active.map((j) => <ActiveJob key={j.id} c={c} job={j} act={act} upload={upload} reload={load} />)}
          </div>
          {data.done.length > 0 && (
            <>
              <h2 className="mt-8 font-display font-bold text-xl text-[#121316]">{c.earn}</h2>
              <div className="mt-3 space-y-2" data-testid="chela-earnings">
                {data.done.map((j) => (
                  <div key={j.id} className="rounded-2xl bg-white border border-[#E5E0D8] px-4 py-3 flex justify-between">
                    <span>{j.service} · {j.date}</span>
                    <span className={`font-bold ${j.payout_status === "paid" ? "text-[#1B8A4B]" : "text-[#E05624]"}`}>₹{j.payout_amount} · {j.payout_status === "paid" ? c.paid : c.due}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {offer && <OfferAlert c={c} job={offer} onAccept={() => act(offer.id, "accept")} onDecline={() => act(offer.id, "decline")} />}
    </div>
  );
}
